import { Children, cloneElement, isValidElement, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { components, GroupBase, MenuListProps } from 'react-select';
import { GroupedVirtuoso, Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { mergeRefs } from '@/utils/merge-refs';

// Each child react-select passes to a grouped MenuList is a <Group> element whose
// own children are the rendered Option elements for that group.
type GroupChildProps = { children: React.ReactNode };

const VirtualizedMenuList = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MenuListProps<Option, IsMulti, Group>
) => {
  const { children, focusedOption, innerRef, selectProps } = props;
  const [menuListEl, setMenuListEl] = useState<HTMLDivElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const childArray = useMemo<ReactElement[]>(
    () => (Array.isArray(children) ? (Children.toArray(children).filter(isValidElement) as ReactElement[]) : []),
    [children]
  );

  const grouped = useMemo(
    () =>
      Array.isArray(selectProps.options) &&
      selectProps.options.some(
        (o) => o && typeof o === 'object' && 'options' in o && Array.isArray((o as { options: unknown }).options)
      ),
    [selectProps.options]
  );

  // Flatten grouped children into ordered Option elements + per-group counts.
  const { groupCounts, flatOptions } = useMemo(() => {
    if (!grouped) return { groupCounts: [] as number[], flatOptions: [] as ReactElement[] };
    const counts: number[] = [];
    const flat: ReactElement[] = [];
    for (const groupEl of childArray) {
      const groupChildren = Children.toArray((groupEl.props as GroupChildProps).children).filter(
        isValidElement
      ) as ReactElement[];
      counts.push(groupChildren.length);
      flat.push(...groupChildren);
    }
    return { groupCounts: counts, flatOptions: flat };
  }, [grouped, childArray]);

  // Translate react-select's focusedOption -> Virtuoso scrollToIndex so keyboard nav
  // still works when the focused row would otherwise be virtualized out.
  const focusedIndex = useMemo(() => {
    const haystack = grouped ? flatOptions : childArray;
    return haystack.findIndex((c) => isValidElement(c) && (c.props as { data?: unknown }).data === focusedOption);
  }, [grouped, childArray, flatOptions, focusedOption]);

  // scrollIntoView is a no-op when the item is already in view. That matters because
  // react-select also updates `focusedOption` on mouse hover, and as Virtuoso shifts
  // rows under the cursor that hover keeps firing — a scrollToIndex here would create
  // an auto-scroll feedback loop.
  useEffect(() => {
    if (focusedIndex >= 0) virtuosoRef.current?.scrollIntoView({ index: focusedIndex });
  }, [focusedIndex]);

  if (childArray.length === 0) {
    return <components.MenuList {...props}>{children}</components.MenuList>;
  }

  const combinedRef = mergeRefs(innerRef, setMenuListEl);

  return (
    <components.MenuList {...props} innerRef={combinedRef}>
      {grouped ? (
        <GroupedVirtuoso
          ref={virtuosoRef}
          customScrollParent={menuListEl ?? undefined}
          groupCounts={groupCounts}
          groupContent={(groupIndex) => {
            const groupEl = childArray[groupIndex];
            if (!groupEl) return null;
            // Re-render the original <Group> element with no option children so only its heading shows.
            return cloneElement(groupEl, {}, null);
          }}
          itemContent={(idx) => flatOptions[idx]}
        />
      ) : (
        <Virtuoso
          ref={virtuosoRef}
          customScrollParent={menuListEl ?? undefined}
          initialItemCount={Math.min(childArray.length, 10)}
          totalCount={childArray.length}
          computeItemKey={(idx) => childArray[idx]?.key ?? idx}
          itemContent={(idx) => childArray[idx]}
        />
      )}
    </components.MenuList>
  );
};

export default VirtualizedMenuList;
